import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMultipleSelection } from 'downshift';

import { RULE_ARITHMETIC_FUNCTION_TYPES } from 'yeself-common/enum/RuleEnum';
import { Role, Language, Location } from 'yeself-common/typings/context/Context';

import SelectField from '../../components/SelectField';

import { AdoptContextRuleValues } from '../types';

type AdoptContextRuleFieldProps = {
    value: AdoptContextRuleValues;
    onChange: (value: AdoptContextRuleValues) => void;
    options: Role[] | Language[] | Location[];
};

const RolesRuleField = ({
    value,
    onChange,
    options,
}: AdoptContextRuleFieldProps): React.ReactElement => {
    const [levelValue, setLevelValue] = useState({
        level0: value.level?.[0]?.data.key || null,
        level1: value.level?.[1]?.data.value || null,
    });

    const formattedOptions = useMemo(
        () => options.map((option) => ({ value: option.name, label: option.name })),
        [options]
    );

    const handleOnChange = useCallback(
        (index: number) => (newValue) => {
            let newLevelValue;
            if (index === 1 && Array.isArray(newValue)) {
                const contextItemValues = newValue.map((item) => item.value);
                newLevelValue = { ...levelValue, [`level${index}`]: contextItemValues };
            } else {
                newLevelValue = { ...levelValue, [`level${index}`]: newValue };
            }

            const level: [
                (
                    | {
                          data: {
                              key:
                                  | typeof RULE_ARITHMETIC_FUNCTION_TYPES.ONE_OF
                                  | typeof RULE_ARITHMETIC_FUNCTION_TYPES.NOT;
                          };
                      }
                    | undefined
                ),
                { data: { value: string[] } } | undefined
            ] = [
                newLevelValue.level0
                    ? {
                          data: {
                              key: newLevelValue.level0 as
                                  | typeof RULE_ARITHMETIC_FUNCTION_TYPES.ONE_OF
                                  | typeof RULE_ARITHMETIC_FUNCTION_TYPES.NOT,
                          },
                      }
                    : undefined,
                newLevelValue.level1
                    ? {
                          data: {
                              value: Array.isArray(newLevelValue.level1) ? newLevelValue.level1 : [],
                          },
                      }
                    : undefined,
            ];

            setLevelValue(newLevelValue);
            onChange({ ...value, level });
        },
        [levelValue, onChange, value]
    );

    const initialSelectedItems = useMemo(() => {
        if (!levelValue.level1) return [];
        const level1Values = Array.isArray(levelValue.level1) ? levelValue.level1 : [];
        return level1Values.map(
            (val) => formattedOptions.find((opt) => opt.value === val) || { value: val, label: val }
        );
    }, [levelValue.level1, formattedOptions]);

    const [inputValue, setInputValue] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const { getSelectedItemProps, addSelectedItem, removeSelectedItem, selectedItems } =
        useMultipleSelection({
            initialSelectedItems,
            onSelectedItemsChange: ({ selectedItems }) => {
                handleOnChange(1)(selectedItems || []);
            },
        });

    const filteredItems = useMemo(() => {
        const remainingItems = formattedOptions.filter(
            (option) => !selectedItems.some((item) => item.value === option.value)
        );

        if (!inputValue) {
            return remainingItems;
        }

        const lowerCasedInput = inputValue.toLowerCase();
        return remainingItems.filter((option) => option.label.toLowerCase().includes(lowerCasedInput));
    }, [inputValue, formattedOptions, selectedItems]);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        if (!isMenuOpen) {
            setIsMenuOpen(true);
        }
    };

    const handleItemSelect = (item) => {
        addSelectedItem(item);
        setInputValue('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && inputValue && filteredItems.length > 0) {
            handleItemSelect(filteredItems[0]);
            e.preventDefault();
            return;
        }

        if (e.key === 'Escape') {
            setIsMenuOpen(false);
            return;
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
            if (inputValue) {
                return;
            }
            if (selectedItems.length > 0) {
                removeSelectedItem(selectedItems[selectedItems.length - 1]);
                e.preventDefault();
            }
        }
    };

    const handleBlur = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsMenuOpen(false);
        }
    };

    return (
        <>
            <SelectField
                autoWidth
                options={[
                    {
                        label: <FormattedMessage id="Rules.level.one_of" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.ONE_OF,
                    },
                    {
                        label: <FormattedMessage id="Rules.level.not_one_of" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.NOT,
                    },
                ]}
                value={levelValue.level0 || ''}
                onChange={handleOnChange(0)}
            />
            {levelValue.level0 ? (
                <div style={{ margin: '10px 0' }}>
                    <div
                        style={{
                            position: 'relative',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                        }}
                        onBlur={handleBlur}
                        tabIndex={-1}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '5px',
                            }}
                        >
                            {selectedItems.map((selectedItem) => (
                                <span
                                    key={`selected-item-${selectedItem.value}`}
                                    {...getSelectedItemProps({ selectedItem, index: selectedItems.indexOf(selectedItem) })}
                                    style={{
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '2px',
                                        padding: '2px 5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {selectedItem.label}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSelectedItem(selectedItem);
                                        }}
                                        aria-label={`Remove ${selectedItem.label}`}
                                        style={{
                                            marginLeft: '5px',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                        }}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            <div style={{ flex: '1 0 50px', minWidth: '50px' }}>
                                <input
                                    onKeyDown={handleKeyDown}
                                    onChange={handleInputChange}
                                    onFocus={() => setIsMenuOpen(true)}
                                    value={inputValue}
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        padding: '4px',
                                        background: 'transparent',
                                    }}
                                    placeholder="Type to search..."
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label="Toggle menu"
                            style={{
                                position: 'absolute',
                                right: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#747474',
                            }}
                        >
                            ▼
                        </button>

                        <ul
                            style={{
                                position: 'absolute',
                                width: '100%',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                margin: '0',
                                padding: '0',
                                listStyle: 'none',
                                backgroundColor: 'white',
                                border: isMenuOpen ? '1px solid #ccc' : 'none',
                                borderRadius: '4px',
                                boxShadow: isMenuOpen ? '0 2px 5px rgba(0, 0, 0, 0.15)' : 'none',
                                zIndex: 1000,
                                display: isMenuOpen ? 'block' : 'none',
                            }}
                        >
                            {isMenuOpen && filteredItems.length > 0 ? (
                                filteredItems.map((item) => (
                                    <li
                                        key={item.value}
                                        onClick={() => handleItemSelect(item)}
                                        style={{
                                            padding: '8px 12px',
                                            backgroundColor: item === filteredItems[0] ? '#f5f5f5' : 'white',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {item.label}
                                    </li>
                                ))
                            ) : isMenuOpen ? (
                                <li style={{ padding: '8px 12px', color: '#999' }}>No options found</li>
                            ) : null}
                        </ul>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default RolesRuleField;
